import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/SEOHead';
import { 
  Plane, Clock, Shield, Star, Phone, MapPin, 
  CheckCircle, ArrowRight, Users, Car, Briefcase
} from 'lucide-react';

const FLEET_IMG = 'https://customer-assets.emergentagent.com/job_f40a108f-f3a3-4735-ac15-18353f3eece6/artifacts/kyn2ki0d_image1.jpeg';
const ESCALADE_IMG = 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/g0b5gai9_IMG_7208.jpg';
const CAPITOL_IMG = 'https://images.pexels.com/photos/31736147/pexels-photo-31736147.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

const schema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Airport Car Service",
  "provider": {
    "@type": "LocalBusiness",
    "name": "Capital Chauffeur Services",
    "telephone": "+1-202-684-0534",
    "email": "contact@capitalchauffeurservices.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Washington",
      "addressRegion": "DC",
      "addressCountry": "US"
    },
    "priceRange": "$$$",
    "image": FLEET_IMG
  },
  "areaServed": {
    "@type": "Place",
    "name": "Ronald Reagan Washington National Airport (DCA)"
  },
  "description": "Professional Reagan National Airport car service with luxury Cadillac Escalade fleet and experienced chauffeurs."
};

export default function ReaganAirportPage() {
  return (
    <div data-testid="reagan-airport-page" className="min-h-screen bg-[#050505]">
      <SEOHead
        title="Reagan Airport Car Service | DCA Limo & Chauffeur | Capital Chauffeur Services"
        description="Professional Reagan National Airport (DCA) car service. Black car pickup & drop-off, curbside meet & greet, corporate accounts. Book your ride today."
        canonical="/reagan-airport-car-service"
        schema={schema}
      />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src={ESCALADE_IMG}
            alt="Black Cadillac Escalade for Reagan National Airport DCA car service"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
          <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4 font-medium">Reagan National Airport</p>
          <h1 data-testid="reagan-h1" className="text-4xl sm:text-5xl lg:text-6xl font-serif text-white mb-6 leading-tight">
            Reagan National Airport<br />Car Service
          </h1>
          <p className="text-[#A3A3A3] text-lg max-w-xl mb-8 leading-relaxed">
            Premium black car service to and from Reagan National Airport. Steps from downtown DC, our chauffeurs provide seamless transfers for business and leisure travelers.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/book">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-8 py-6 text-sm font-medium tracking-wider uppercase">
                Reserve Your Transfer
              </Button>
            </Link>
            <a href="tel:+12026840534">
              <Button className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black rounded-none px-8 py-6 text-sm font-medium tracking-wider uppercase bg-transparent inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> (202) 684-0534
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* About DCA */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-3">About DCA</p>
            <h2 className="text-3xl font-serif text-white mb-6">Ronald Reagan Washington National Airport</h2>
            <div className="space-y-4 text-[#A3A3A3] leading-relaxed">
              <p>
                Ronald Reagan Washington National Airport sits on the banks of the Potomac River in Arlington, Virginia, just four miles south of downtown Washington, DC. Its proximity to the nation's capital makes it the preferred airport for business travelers, government officials, and lobbyists who need quick access to Capitol Hill, the White House, and K Street.
              </p>
              <p>
                DCA primarily handles domestic flights, with service to over 95 destinations across the United States. The airport processes more than 25 million passengers annually and is served by all major domestic carriers. Its compact terminal layout and Metro accessibility make it exceptionally convenient for travelers who value efficiency.
              </p>
              <p>
                Capital Chauffeur Services offers premium black car transportation from Reagan National to all points in the DC metro area. Our chauffeurs stage near the airport and monitor your flight status, ensuring a prompt pickup regardless of delays or early arrivals. From DCA, most destinations in Washington, DC are just 10 to 20 minutes away.
              </p>
            </div>
          </div>
          <div className="aspect-[4/3] overflow-hidden">
            <img
              src={CAPITOL_IMG}
              alt="United States Capitol building near Reagan National Airport Washington DC"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-serif text-white mb-4">Reagan Airport Transportation Services</h2>
            <p className="text-[#A3A3A3] max-w-2xl mx-auto">
              From quick curbside pickups to full meet-and-greet service, our DCA airport car service adapts to your schedule and preferences.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Plane, title: 'DCA Arrivals Pickup', desc: 'Your chauffeur monitors your flight and waits at the designated pickup area. No standing in taxi lines or waiting for rideshares at Terminal A, B, or C.' },
              { icon: Clock, title: 'Departures Drop-Off', desc: 'Timely curbside drop-off at your terminal entrance. We account for traffic patterns on the George Washington Parkway and Route 1 to ensure you arrive stress-free.' },
              { icon: Shield, title: 'VIP Meet & Greet', desc: 'Our chauffeur meets you at baggage claim with a name sign, helps with your luggage, and walks you to your waiting black Escalade.' },
              { icon: Briefcase, title: 'Congressional & Government', desc: 'Trusted transportation for members of Congress, staffers, lobbyists, and government contractors. Discreet, professional, and always on time.' },
              { icon: Users, title: 'Group Transfers', desc: 'Accommodating executive teams and small delegations. Our Escalade ESV comfortably seats up to six passengers with luggage.' },
              { icon: Car, title: 'Hourly Service from DCA', desc: 'Need multiple stops after landing? Book our hourly chauffeur service for meetings across DC before heading to your hotel.' },
            ].map((service, i) => (
              <div key={i} className="border border-white/10 p-8 hover:border-[#D4AF37]/30 transition-colors">
                <service.icon className="w-8 h-8 text-[#D4AF37] mb-4" />
                <h3 className="text-white font-serif text-lg mb-3">{service.title}</h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Routes */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-serif text-white mb-8 text-center">Popular Routes from Reagan National</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { route: 'DCA to Capitol Hill', time: '10-15 min' },
              { route: 'DCA to Georgetown', time: '15-25 min' },
              { route: 'DCA to K Street NW', time: '12-20 min' },
              { route: 'DCA to The Wharf', time: '10-15 min' },
              { route: 'DCA to Tysons Corner', time: '25-40 min' },
              { route: 'DCA to Bethesda, MD', time: '25-35 min' },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-between border border-white/10 p-5">
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-white text-sm">{item.route}</span>
                </div>
                <span className="text-[#D4AF37] text-xs">{item.time}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="aspect-[21/9] overflow-hidden">
            <img
              src={FLEET_IMG}
              alt="Capital Chauffeur Services luxury fleet for Reagan Airport car service DCA"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-serif text-white mb-10 text-center">Why Travelers Choose Capital Chauffeur at DCA</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              'Closest airport to downtown DC means shorter rides and lower fares compared to Dulles or BWI transfers',
              'Professional chauffeurs with security clearance experience, trusted by government agencies and embassies',
              'Real-time flight monitoring with automatic pickup adjustments for delays or early arrivals',
              'Premium black Cadillac Escalade ESV with tinted windows, leather seating, and onboard amenities',
              'Curbside pickup within minutes of your call or text — no surge pricing, no shared rides',
              'Available around the clock for early morning shuttle flights and late-night red-eye arrivals',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-4">
                <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <p className="text-[#A3A3A3] leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-serif text-white mb-4">Explore Our Other Services</h2>
          <p className="text-[#A3A3A3] mb-10 max-w-xl mx-auto">
            Premium ground transportation across the entire Washington, DC metropolitan area.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { label: 'Dulles Airport Car Service', path: '/dulles-airport-car-service' },
              { label: 'Corporate Chauffeur NoVA', path: '/corporate-chauffeur-northern-virginia' },
              { label: 'Black Car Service Arlington', path: '/black-car-service-arlington' },
              { label: 'Black Car Service Tysons', path: '/black-car-service-tysons' },
              { label: 'All Services', path: '/services' },
            ].map((link, i) => (
              <Link key={i} to={link.path}>
                <Button variant="outline" className="border-white/20 text-[#A3A3A3] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-none px-6 py-3 text-xs tracking-wider uppercase bg-transparent inline-flex items-center gap-2">
                  {link.label} <ArrowRight className="w-3 h-3" />
                </Button>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-serif text-white mb-4">Reserve Your Reagan Airport Transfer</h2>
          <p className="text-[#A3A3A3] mb-8">
            Skip the taxi lines and rideshare surge pricing. Book a premium black car from Reagan National Airport today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-10 py-6 text-sm font-medium tracking-wider uppercase">
                Book Now
              </Button>
            </Link>
            <a href="tel:+12026840534">
              <Button className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black rounded-none px-10 py-6 text-sm font-medium tracking-wider uppercase bg-transparent inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> Call (202) 684-0534
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
